import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ShieldCheck } from 'lucide-react';
import type { TlsContextInfo } from '@sbclite/core';
import { useCACertificatesForDropdown, useCertificatesForDropdown } from '../../hooks/useCertificates';
import { useCreateTlsContext, useUpdateTlsContext } from '../../hooks/useTlsContexts';
import { Modal } from '../common';

const tlsContextFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional().nullable(),
  enabled: z.boolean(),
  certificateId: z.string().optional().nullable(),
  caCertificateId: z.string().optional().nullable(),
  minVersion: z.enum(['TLSv1.2', 'TLSv1.3']),
  maxVersion: z.enum(['TLSv1.2', 'TLSv1.3']).optional().nullable(),
  cipherOrderText: z.string().optional(),
  verifyPeer: z.boolean(),
  verifyHostname: z.boolean(),
  sni: z.string().optional().nullable(),
  clientCertificateRequired: z.boolean(),
});

type TlsContextFormData = z.infer<typeof tlsContextFormSchema>;

function parseCipherOrder(cipherOrderText: string | undefined): string[] | null {
  const values = (cipherOrderText ?? '')
    .split(/[\n,]/)
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  return values.length > 0 ? values : null;
}

function buildDefaultValues(editingTlsContext: TlsContextInfo | null): TlsContextFormData {
  return {
    name: editingTlsContext?.name ?? '',
    description: editingTlsContext?.description ?? '',
    enabled: editingTlsContext?.enabled ?? true,
    certificateId: editingTlsContext?.certificateId ?? '',
    caCertificateId: editingTlsContext?.caCertificateId ?? '',
    minVersion: editingTlsContext?.minVersion ?? 'TLSv1.2',
    maxVersion: editingTlsContext?.maxVersion ?? null,
    cipherOrderText: editingTlsContext?.cipherOrder?.join(', ') ?? '',
    verifyPeer: editingTlsContext?.verifyPeer ?? true,
    verifyHostname: editingTlsContext?.verifyHostname ?? true,
    sni: editingTlsContext?.sni ?? '',
    clientCertificateRequired: editingTlsContext?.clientCertificateRequired ?? false,
  };
}

export function TlsContextEditor({
  isOpen,
  onClose,
  onSuccess,
  editingTlsContext,
}: {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly editingTlsContext: TlsContextInfo | null;
}) {
  const createMutation = useCreateTlsContext();
  const updateMutation = useUpdateTlsContext();
  const { data: serverCertificates = [] } = useCertificatesForDropdown('server');
  const { data: caCertificates = [] } = useCACertificatesForDropdown();

  const form = useForm<TlsContextFormData>({
    defaultValues: buildDefaultValues(editingTlsContext),
  });

  useEffect(() => {
    form.reset(buildDefaultValues(editingTlsContext));
  }, [editingTlsContext, form]);

  async function onSubmit(values: TlsContextFormData) {
    const payload = {
      name: values.name.trim(),
      description: values.description?.trim() ? values.description.trim() : null,
      enabled: values.enabled,
      certificateId: values.certificateId?.trim() ? values.certificateId.trim() : null,
      caCertificateId: values.caCertificateId?.trim() ? values.caCertificateId.trim() : null,
      minVersion: values.minVersion,
      maxVersion: values.maxVersion && values.maxVersion.length > 0 ? values.maxVersion : null,
      cipherOrder: parseCipherOrder(values.cipherOrderText),
      verifyPeer: values.verifyPeer,
      verifyHostname: values.verifyHostname,
      sni: values.sni?.trim() ? values.sni.trim() : null,
      clientCertificateRequired: values.clientCertificateRequired,
    };

    if (editingTlsContext) {
      await updateMutation.mutateAsync({ id: editingTlsContext.id, input: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }

    onSuccess();
    onClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingTlsContext ? 'Edit TLS Context' : 'Create TLS Context'}
      subtitle="Reusable TLS policy for SIP interfaces and peers"
      size="lg"
    >
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
            <input {...form.register('name')} className="w-full rounded-md border border-gray-300 px-3 py-2" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea {...form.register('description')} rows={3} className="w-full rounded-md border border-gray-300 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Server certificate</label>
            <select {...form.register('certificateId')} className="w-full rounded-md border border-gray-300 px-3 py-2 bg-white">
              <option value="">None</option>
              {serverCertificates.map((certificate) => (
                <option key={certificate.value} value={certificate.value}>{certificate.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">CA certificate</label>
            <select {...form.register('caCertificateId')} className="w-full rounded-md border border-gray-300 px-3 py-2 bg-white">
              <option value="">None</option>
              {caCertificates.map((certificate) => (
                <option key={certificate.value} value={certificate.value}>{certificate.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Min version</label>
            <select {...form.register('minVersion')} className="w-full rounded-md border border-gray-300 px-3 py-2 bg-white">
              <option value="TLSv1.2">TLSv1.2</option>
              <option value="TLSv1.3">TLSv1.3</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Max version</label>
            <select {...form.register('maxVersion')} className="w-full rounded-md border border-gray-300 px-3 py-2 bg-white">
              <option value="">Unbounded</option>
              <option value="TLSv1.2">TLSv1.2</option>
              <option value="TLSv1.3">TLSv1.3</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Cipher order</label>
            <textarea {...form.register('cipherOrderText')} rows={3} className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm" placeholder="TLS_AES_256_GCM_SHA384, TLS_CHACHA20_POLY1305_SHA256" />
          </div>
          <div className="md:col-span-2 flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" {...form.register('verifyPeer')} /> Verify peer</label>
            <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" {...form.register('verifyHostname')} /> Verify hostname</label>
            <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" {...form.register('clientCertificateRequired')} /> Client cert required</label>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">SNI</label>
            <input {...form.register('sni')} className="w-full rounded-md border border-gray-300 px-3 py-2" placeholder="sip.example.com" />
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            <input type="checkbox" {...form.register('enabled')} className="h-4 w-4" />
            <span className="text-sm text-gray-700">Enabled</span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t pt-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <ShieldCheck className="h-4 w-4" />
            No secret material is stored in backups or shown here.
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm">Cancel</button>
            <button type="submit" className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white" disabled={createMutation.isPending || updateMutation.isPending}>
              {editingTlsContext ? 'Save' : 'Create'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
